import { useMutation } from '@apollo/client';

import { Field, Form, Formik } from 'formik';
import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Components
import ButtonComponent from '../../../components/UI/Button/Button.component';
import UserProfileAvatar from '../../../components/UserProfileAvatar/index';
import { ErrorMessages } from '../../../constants/error-messages';
import { FORM_IDS } from '../../../constants/form-ids.constants';
import { GLOBAL_CONSTANTS } from '../../../constants/globals';
// Constants and Utils
import { sectionIDs } from '../../../constants/section-ids.constants';
import { SuccessMessages } from '../../../constants/success-messages.constants';
// GraphQL
import { UPDATE_USER_PREFERENCES_MUTATION } from '../../../graphql/resolvers/userMutations/updateUserPreferences.gql';
// Types
import type {
  UserPreferences,
  UserState,
} from '../../../interfaces/user.interface';
import type { UserAvatarState } from '../../../interfaces/userPreferences.interface';
// Services
import {
  getAvatarImageDataUrl,
  UPLOAD_ERRORS,
  uploadAvatar,
  uploadEditedAvatar,
} from '../../../services/getPresignedUrlREST';
// Store
import { userSlice } from '../../../store/user.store';
import { userAvatarSlice } from '../../../store/userAvatar.store';

// Styles
import './preferences-view.scss';

// Initial state
import { userPreferencesInitialState } from '../../../initial-states/userPreferences.initialState';

/**
 * The PreferencesView component is responsible for rendering and handling the user preferences view.
 * It allows the user to update their preferences, such as theme, landing page, and avatar.
 *
 * @return {React.ReactElement} The rendered PreferencesView component
 */
export const PreferencesView = (): React.ReactElement => {
  // Redux
  const dispatch = useDispatch();
  const { UPDATE_USER } = userSlice.actions;
  const {
    reloadAvatar,
    loadingAvatar,
    userAvatarKey,
    userAvatarUrl,
    newAvatar,
  } = useSelector((state: UserAvatarState) => state.userAvatarStore);
  const { user } = useSelector((state: UserState) => state.userStore);
  const {
    SET_RELOAD_AVATAR,
    SET_USER_AVATAR_KEY,
    SET_USER_AVATAR_URL,
    SET_NEW_AVATAR,
  } = userAvatarSlice.actions;

  // Local state
  const { userPreferences } = user;
  const [uploading, setUploading] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [userPrefs, setUserPrefs] = useState<UserPreferences>(
    userPreferences as UserPreferences,
  );
  const [isEditingAvatar, setIsEditingAvatar] = useState(false);
  const [isChangingAvatar, setIsChangingAvatar] = useState(false);
  const [editInitialImage, setEditInitialImage] = useState<string | null>(null);

  // Success messages
  const { PRE_SIGNED_URL_SUCCESS } = SuccessMessages;

  // Effect to update user preferences when avatar changes
  useEffect(() => {
    if (userAvatarKey === null || reloadAvatar) {
      setUserPrefs(userPreferences as UserPreferences);
    }
  }, [userAvatarKey, reloadAvatar, userPreferences]);

  // User preferences mutation
  const [updateUserPreferences] = useMutation(
    UPDATE_USER_PREFERENCES_MUTATION,
    {
      variables: {
        input: {
          userId: user.id,
          theme: userPrefs.theme?.toUpperCase(),
          landingPage: userPrefs.landingPage?.toUpperCase(),
        },
      },
      onCompleted: ({ updateUserPreferences }) => {
        if (updateUserPreferences) {
          dispatch(
            UPDATE_USER({
              ...user,
              userPreferences: {
                theme: updateUserPreferences.theme,
                landingPage: updateUserPreferences.landingPage,
              },
            }),
          );
        }
      },
      onError: (ex) => {
        console.error('Error updating preferences:', ex);
      },
    },
  );

  // Handle saving preferences
  const handleSavePreferences = (values: {
    theme: string;
    landingPage: string;
  }) => {
    const uppercasedValues = {
      theme: values.theme,
      landingPage: values.landingPage,
    };

    const payload = {
      input: {
        ...uppercasedValues,
        userId: user.id,
      },
    };

    setSavingPreferences(true);
    updateUserPreferences({ variables: payload })
      .then((res) => {
        console.log('Preferences updated:', res);
      })
      .catch((error) => {
        console.error('Failed to update preferences:', error);
      })
      .finally(() => {
        setSavingPreferences(false);
      });
  };

  // Avatar handlers
  const handleChangeAvatar = () => {
    setIsChangingAvatar(true);
  };

  const handleCancelChangeAvatar = () => {
    setIsChangingAvatar(false);
    setFile(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const selectedFile: File | undefined = event.target.files[0];
      if (selectedFile !== undefined) {
        setFile(selectedFile);
      } else {
        alert(UPLOAD_ERRORS.INVALID_FILE_TYPE);
      }
    }
  };

  const handleUpload = async (): Promise<void> => {
    if (!file) {
      alert(UPLOAD_ERRORS.SELECT_FILE);
      return;
    }

    setUploading(true);

    try {
      // Use the service to handle the upload
      const result = await uploadAvatar(file, user.id, updateUserPreferences);

      // Update Redux state with new avatar information
      dispatch(SET_USER_AVATAR_KEY(result.key));
      dispatch(UPDATE_USER({ ...user, avatar: result.key }));
      dispatch(SET_USER_AVATAR_URL(result.url));
      dispatch(SET_NEW_AVATAR(result.url));
      dispatch(SET_RELOAD_AVATAR(true));

      alert(PRE_SIGNED_URL_SUCCESS.DEFAULT);
      setIsChangingAvatar(false);
    } catch (error) {
      alert(
        error instanceof Error ? error.message : UPLOAD_ERRORS.FAILED_TO_UPLOAD,
      );
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
      setFile(null);
    }
  };

  const handleEditAvatar = async () => {
    if (userAvatarKey) {
      try {
        const dataUrl = await getAvatarImageDataUrl(userAvatarKey);
        setEditInitialImage(dataUrl);
      } catch {
        setEditInitialImage(null);
      }
    }
    setIsEditingAvatar(true);
  };

  const handleSaveEditedAvatar = async (editedImageData: string) => {
    setIsEditingAvatar(false);
    setEditInitialImage(null);
    setUploading(true);

    try {
      // Use the service to handle the edited avatar upload
      const result = await uploadEditedAvatar(
        editedImageData,
        user.id,
        updateUserPreferences,
      );

      // Update Redux state with new avatar information
      dispatch(SET_USER_AVATAR_KEY(result.key));
      dispatch(UPDATE_USER({ ...user, avatar: result.key }));
      dispatch(SET_USER_AVATAR_URL(result.url));
      dispatch(SET_RELOAD_AVATAR(true));

      setIsChangingAvatar(false);
      setFile(null);
      alert(SuccessMessages.AVATAR.UPDATED);
    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : ErrorMessages.AVATAR.UPDATE_FAILED,
      );
      console.error('Error saving edited avatar:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleCancelEditAvatar = () => {
    setIsEditingAvatar(false);
    setEditInitialImage(null);
  };

  const onSaveEditedAvatarHandler = () => {
    console.log('onSaveEditedAvatarHandler');
  };

  return (
    <section
      id={sectionIDs.SETTINGS_VIEW}
      className={'USER__SETTINGS__PREFERENCES'}
    >
      <div className="Grid">
        <div className="Preferences__View__Wrapper">
          <Formik
            // TRIL-150: userPreferences hydrates AFTER mount on a cold /settings
            // load (UserHydrationGate fires GET_USER_QUERY async). Without
            // enableReinitialize, Formik keeps the empty mount-time initialValues
            // and the landing-page radios render blank even though the value is
            // persisted server-side. Reinitialize when the hydrated value arrives.
            // `avatar` is intentionally NOT in these initialValues: this form
            // edits only landingPage/theme (handleSavePreferences sends just
            // those). Including it would let an avatar upload — which dispatches
            // user.avatar to the store before any save — reinitialize this form
            // and silently revert an unsaved radio selection.
            enableReinitialize
            initialValues={{
              ...userPreferencesInitialState,
              landingPage: (userPreferences as UserPreferences).landingPage,
              theme: (userPreferences as UserPreferences).theme,
            }}
            onSubmit={(values, actions) => {
              handleSavePreferences(
                values as { theme: string; landingPage: string },
              );
            }}
          >
            {({ errors, touched, isValid, values }) => {
              return (
                <Form
                  className="Preferences__Form"
                  id={FORM_IDS.PREFERENCES.URI_RADIO_GROUP}
                >
                  <p>Landing page</p>
                  <div
                    aria-labelledby={FORM_IDS.PREFERENCES.URI_RADIO_GROUP}
                    role="group"
                  >
                    <label>
                      <Field
                        checked={values.landingPage?.toLowerCase() === 'feed'}
                        name="landingPage"
                        type="radio"
                        value="FEED"
                      />
                      Feed - <strong>Default </strong>
                    </label>
                    <br />
                    <label>
                      <Field
                        checked={
                          values.landingPage?.toLowerCase() === 'dashboard'
                        }
                        name="landingPage"
                        type="radio"
                        value="DASHBOARD"
                      />
                      Dashboard
                    </label>
                  </div>
                  {savingPreferences ? (
                    <div className="LoadingSpinner" />
                  ) : (
                    <ButtonComponent
                      className="Preferences__Update__Button"
                      color="success"
                      disabled={!touched || !isValid}
                      type="submit"
                      variant="primary"
                    >
                      {GLOBAL_CONSTANTS.SAVE}
                    </ButtonComponent>
                  )}
                </Form>
              );
            }}
          </Formik>
        </div>
        <div className="Preferences__AVATAR">
          <Formik
            // TRIL-150: same hydration timing as the preferences form above —
            // reinitialize so this form doesn't submit a stale (empty)
            // landingPage/theme captured before the user slice hydrated.
            enableReinitialize
            initialValues={{
              ...userPreferencesInitialState,
              landingPage: (userPreferences as UserPreferences).landingPage,
              theme: (userPreferences as UserPreferences).theme,
              avatar: user.avatar, // TRIL-156: read avatar key from root user, not nested userPreferences
            }}
            onSubmit={(values, actions) => {
              if (values) {
                handleSavePreferences(
                  values as { theme: string; landingPage: string },
                );
              }
            }}
          >
            {({ errors, touched, isValid, values }) => {
              return uploading || reloadAvatar ? (
                <div className="LoadingSpinner" />
              ) : (
                <Form
                  className="Preferences__Form__Avatar"
                  id="Preferences__Avatar"
                >
                  <UserProfileAvatar
                    editInitialImage={editInitialImage}
                    file={file}
                    firstName={user.firstName}
                    lastName={user.lastName}
                    handleCancelChangeAvatar={handleCancelChangeAvatar}
                    handleCancelEditAvatar={handleCancelEditAvatar}
                    handleChangeAvatar={handleChangeAvatar}
                    handleEdit={handleEditAvatar}
                    handleFileChange={handleFileChange}
                    handleSaveEdited={handleSaveEditedAvatar}
                    handleUpload={handleUpload as any}
                    isChangingAvatar={isChangingAvatar}
                    isEditingAvatar={isEditingAvatar}
                    isValid={isValid}
                    loadingAvatar={loadingAvatar}
                    newAvatar={newAvatar}
                    touched={touched}
                    uploading={uploading}
                    userAvatarKey={userAvatarKey as string}
                    userAvatarUrl={userAvatarUrl as string}
                    onSave={onSaveEditedAvatarHandler}
                  />
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </section>
  );
};
